import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  LIST_CALENDAR_RULER,
  UPDATE_CALENDAR_RULERS,
  GET_SCHEDULES_NEXT_N_DAYS,
  CANCEL_SCHEDULE,
} from '../../../../services/schedule';
import { publicClient } from '~/services';
import ScheduleLinkGeneratorDialog from '../../../shared/ScheduleLinkGeneratorDialog';
import CancelAppointmentDialog from '../../../shared/CancelAppointmentDialog';

interface Schedule {
  id: string;
  title: string;
  description: string;
  isEnabled: boolean;
  meetingType: string;
  duration_of_meeting: number;
  max_users_per_meeting: number;
}

interface UpcomingAppointment {
  id: string;
  name: string;
  phone: string;
  scheduledDate: Date;
  scheduleName: string;
}

const ScheduleHome: React.FC = () => {
  const [t] = useTranslation('schedule');
  const { agentId } = useParams();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isPhoneLinkDialogOpen, setIsPhoneLinkDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<{ id: string; title: string } | null>(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState<UpcomingAppointment[]>([]);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<UpcomingAppointment | null>(null);

  const { data, loading, refetch } = useQuery(LIST_CALENDAR_RULER, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: upcomingData, loading: upcomingLoading } = useQuery(GET_SCHEDULES_NEXT_N_DAYS, {
    variables: {
      data: {
        agent_id: agentId,
        days: 7, // Fetch for the next 7 days
      },
    },
    fetchPolicy: 'network-only',
    skip: !agentId,
  });
  
  const [updateCalendarRuler, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_CALENDAR_RULERS,
  );

  const [cancelScheduleMutation, { loading: cancelLoading }] = useMutation(CANCEL_SCHEDULE, {
    client: publicClient,
  });
  
  useEffect(() => {
    if (data?.response?.data && !data.response.hasError) {
      const calendarRulers = data.response.data;
      
      const formattedSchedules = calendarRulers.map((ruler: any) => ({
        id: ruler._id,
        title: ruler.title,
        description: ruler.description,
        isEnabled: ruler.isEnabled,
        meetingType: ruler.meetingType,
        duration_of_meeting: ruler.duration_of_meeting,
        max_users_per_meeting: ruler.max_users_per_meeting
      }));
      
      setSchedules(formattedSchedules);
    }
  }, [data]);

  useEffect(() => {
    if (upcomingData?.get_schedules_next_n_days?.data && !upcomingData.get_schedules_next_n_days.hasError) {
      const appointments = upcomingData.get_schedules_next_n_days.data;
      
      const formattedAppointments: UpcomingAppointment[] = appointments.map((appt: any) => ({
        id: appt._id,
        name: appt.name,
        phone: appt.people_channel_id?.channel_id || '',
        scheduledDate: new Date(appt.start_date.date),
        scheduleName: appt.calendar_rulers_id?.title || 'General',
      }));
      
      setUpcomingAppointments(formattedAppointments);
    }
  }, [upcomingData]);

  const toggleScheduleStatus = async (id: string, currentStatus: boolean) => {
    try {
      // Set the current schedule being updated
      setUpdatingId(id);
      setErrorMessage(null);
      
      // Find the current schedule in our local state
      const scheduleToUpdate = schedules.find(schedule => schedule.id === id);
      
      if (!scheduleToUpdate) {
        throw new Error('Schedule not found');
      }
      
      // Create update payload with all required fields
      const updateData = {
        id: id,
        agent_id: agentId,
        title: scheduleToUpdate.title,
        description: scheduleToUpdate.description,
        meetingType: scheduleToUpdate.meetingType,
        duration_of_meeting: scheduleToUpdate.duration_of_meeting,
        meeting_duration_in_mins: scheduleToUpdate.duration_of_meeting,
        max_users_per_meeting: scheduleToUpdate.max_users_per_meeting,
        isEnabled: !currentStatus
      };
      
      // Call the mutation to update the schedule status
      const { data } = await updateCalendarRuler({
        variables: {
          data: updateData
        }
      });
      
      // Check if update was successful
      if (data && !data.update_calendar_rulers.hasError) {
        // Update local state to reflect the new status
        setSchedules(schedules.map(schedule => 
          schedule.id === id 
            ? { ...schedule, isEnabled: !schedule.isEnabled } 
            : schedule
        ));
        console.log('Schedule status updated successfully');
      } else {
        console.error('Error updating schedule status:', data?.update_calendar_rulers.message);
        // Show error message to user
        setErrorMessage(t('update_error') || 'Error updating schedule status');
      }
    } catch (err) {
      console.error('Error updating schedule status:', err);
      // Show error message to user
      setErrorMessage(t('update_error') || 'Error updating schedule status');
    } finally {
      // Clear the updating state regardless of outcome
      setUpdatingId(null);
    }
  };

  const handleCreateGeneralLink = () => {
    // Open the phone dialog instead of directly generating link
    setSelectedSchedule(null); // Clear any selected schedule for general link
    setIsPhoneLinkDialogOpen(true);
  };

  const handleGenerateScheduleLink = (scheduleId: string, scheduleTitle: string) => {
    console.log('handleGenerateScheduleLink called with:', { scheduleId, scheduleTitle });
    setSelectedSchedule({ id: scheduleId, title: scheduleTitle });
    setIsPhoneLinkDialogOpen(true);
  };

  const handleLinkGenerated = (link: string) => {
    console.log('General link generated:', link);
    setSuccessMessage(t('link_generated'));
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const handleCancelAppointment = (appointment: UpcomingAppointment) => {
    setSelectedAppointment(appointment);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancellation = async (reason: string) => {
    if (!selectedAppointment) return;

    try {
      console.log('Canceling appointment:', {
        appointmentId: selectedAppointment.id,
        reason,
        appointmentDetails: {
          name: selectedAppointment.name,
          phone: selectedAppointment.phone,
          scheduledDate: selectedAppointment.scheduledDate,
        }
      });

      // Call the cancel schedule mutation
      const { data: cancelResult } = await cancelScheduleMutation({
        variables: {
          data: {
            schedule_id: selectedAppointment.id,
            cancel_reason: reason,
            cancel_by: 'HUMAN_AGENT', // Since this is being cancelled by the agent from the admin panel
          }
        }
      });

      if (cancelResult?.cancel_schedule?.hasError) {
        throw new Error(cancelResult.cancel_schedule.message || 'Error canceling appointment');
      }

      // Update local state to remove the canceled appointment
      setUpcomingAppointments(prevAppointments => 
        prevAppointments.filter(appt => appt.id !== selectedAppointment.id)
      );

      setSuccessMessage(t('appointment_canceled_successfully') || 'Cita cancelada exitosamente');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

    } catch (error) {
      console.error('Error canceling appointment:', error);
      // Show error message to user
      setSuccessMessage(null);
      // TODO: You might want to add an error state and show error toast
      alert(`Error al cancelar la cita: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
    setSelectedAppointment(null);
  };

  return (
    <div className="flex flex-col">
      <h3 className="text-lg font-semibold mb-4">{t('upcoming_appointments')}</h3>
      {upcomingLoading ? (
        <div className="text-center p-10 bg-base-200 rounded-lg">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : upcomingAppointments.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t('client')}</th>
                <th>{t('scheduled_date')}</th>
                <th>{t('schedule_name')}</th>
                <th>{t('actions') || 'Acciones'}</th>
              </tr>
            </thead>
            <tbody>
              {upcomingAppointments.map((appt) => (
                <tr key={appt.id} className="hover">
                  <td>
                    <div className="font-bold">{appt.name}</div>
                    <div className="text-sm opacity-50">{appt.phone}</div>
                  </td>
                  <td>{formatDate(appt.scheduledDate)}</td>
                  <td>{appt.scheduleName}</td>
                  <td>
                    <button
                      onClick={() => handleCancelAppointment(appt)}
                      className="btn btn-ghost btn-xs"
                      title={t('cancel_appointment') || 'Cancelar cita'}
                      disabled={cancelLoading}
                    >
                      {cancelLoading ? (
                        <span className="loading loading-spinner loading-xs mr-1"></span>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                      {t('cancel') || 'Cancelar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-10 bg-base-200 rounded-lg">
          <p className="text-lg">{t('no_appointments')}</p>
        </div>
      )}

      <div className="divider my-8"></div>

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{t('schedule_list')}</h3>
        <div className="flex gap-2">
          {schedules.length > 0 && (
            <button 
              className="btn btn-sm btn-secondary"
              onClick={handleCreateGeneralLink}
              title="Create general link for all schedules"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {t('create_general_link')}
            </button>
          )}
          <button 
            className="btn btn-sm btn-outline" 
            onClick={() => refetch && refetch()}
            disabled={loading}
          >
            {loading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span className="ml-1">{t('refresh')}</span>
          </button>
        </div>
      </div>
      
      {loading ? (
        // Loading skeleton
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="card bg-base-100 shadow-xl animate-pulse">
              <div className="card-body">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="card-actions justify-end mt-4">
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : schedules.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schedules.map((schedule) => (
            <div key={schedule.id} className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <div className="flex justify-between items-start">
                  <h2 className="card-title">{schedule.title}</h2>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${schedule.isEnabled ? 'badge-success' : 'badge-secondary'}`}>
                      {schedule.isEnabled ? t('active') : t('inactive')}
                    </span>
                    <div className="form-control">
                      <label className="cursor-pointer label p-0">
                        <input 
                          type="checkbox" 
                          className="toggle toggle-primary toggle-sm" 
                          checked={schedule.isEnabled}
                          onChange={() => toggleScheduleStatus(schedule.id, schedule.isEnabled)}
                          disabled={updatingId === schedule.id}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                <p className="my-2">{schedule.description}</p>
                <div className="text-sm text-gray-500">
                  <p>{schedule.meetingType === 'VIRTUAL' ? t('virtual_meeting') : t('in_person_meeting')}</p>
                  <p>{schedule.duration_of_meeting} {t('minutes')}</p>
                </div>
                <div className="card-actions justify-end mt-2">
                  <button
                    onClick={() => handleGenerateScheduleLink(schedule.id, schedule.title)}
                    className="btn btn-secondary btn-sm"
                    title={t('generate_link')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                     
                  </button>
                  <Link to={`/agent/${agentId}/schedule/edit/${schedule.id}`} className="btn btn-primary btn-sm">
                    {t('edit')}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-10 bg-base-200 rounded-lg">
          <p className="text-lg">{t('no_schedules')}</p>
          <Link to={`/agent/${agentId}/schedule/create`} className="btn btn-primary mt-4">
            {t('create_new')}
          </Link>
        </div>
      )}

      {/* Mensaje de error si hay problemas al actualizar */}
      {errorMessage && (
        <div className="alert alert-error shadow-lg mb-6">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{errorMessage}</span>
          </div>
          <div className="flex-none">
            <button 
              onClick={() => setErrorMessage(null)} 
              className="btn btn-sm btn-ghost"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Mensaje de éxito */}
      {successMessage && (
        <div className="alert alert-success shadow-lg mb-6">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{successMessage}</span>
          </div>
          <div className="flex-none">
            <button 
              onClick={() => setSuccessMessage(null)} 
              className="btn btn-sm btn-ghost"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="divider my-8"></div>

      <div className="bg-base-200 p-6 rounded-lg mt-8">
        <h2 className="text-xl font-semibold mb-3">{t('schedule_explanation')}</h2>
        <p className="mb-4">
          {t('schedule_description')}
        </p>
        <Link to={`/agent/${agentId}/schedule/create`} className="btn btn-primary">
          {t('create_new')}
        </Link>
      </div>


      {/* Schedule Link Generator Dialog */}
      <ScheduleLinkGeneratorDialog
        isOpen={isPhoneLinkDialogOpen}
        onClose={() => {
          setIsPhoneLinkDialogOpen(false);
          setSelectedSchedule(null);
        }}
        agentId={agentId!}
        calendar_rulers_id={selectedSchedule?.id}
        scheduleTitle={selectedSchedule?.title}
        onLinkGenerated={handleLinkGenerated}
      />

      {/* Cancel Appointment Dialog */}
      {selectedAppointment && (
        <CancelAppointmentDialog
          isOpen={isCancelDialogOpen}
          onClose={handleCloseCancelDialog}
          appointmentName={selectedAppointment.name}
          appointmentPhone={selectedAppointment.phone}
          scheduledDate={selectedAppointment.scheduledDate}
          onConfirmCancel={handleConfirmCancellation}
        />
      )}
    </div>
  );
};

export default ScheduleHome;
